// audio-encoder.js
// Gestion de l'encodage MP3 et conversion audio

/**
 * Encode des données audio en MP3 avec lamejs
 */
export async function optimizedMp3Encode(audioData, sampleRate, channels, progressCallback) {
    if (!window.lamejs) {
      throw new Error('La bibliothèque lamejs n\'est pas disponible');
    }
  
    try {
      const mp3encoder = new lamejs.Mp3Encoder(channels, sampleRate, 128);
      const mp3Data = [];
      const samples = new Int16Array(audioData.length);
      
      // Conversion et normalisation des données audio
      for (let i = 0; i < audioData.length; i++) {
        const sample = Math.max(-1, Math.min(1, audioData[i]));
        samples[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      }
  
      // Traitement par blocs
      const blockSize = 1152;
      const numBlocks = Math.ceil(samples.length / blockSize);
  
      for (let i = 0; i < numBlocks; i++) {
        if (progressCallback) {
          progressCallback((i / numBlocks) * 100);
        }
  
        const start = i * blockSize;
        const end = Math.min(start + blockSize, samples.length);
        const leftChunk = samples.slice(start, end);
        
        if (leftChunk.length < blockSize) {
          const paddedChunk = new Int16Array(blockSize);
          paddedChunk.set(leftChunk);
          const mp3buf = mp3encoder.encodeBuffer(paddedChunk);
          if (mp3buf.length > 0) mp3Data.push(mp3buf);
        } else {
          const mp3buf = mp3encoder.encodeBuffer(leftChunk);
          if (mp3buf.length > 0) mp3Data.push(mp3buf);
        }
  
        // Pause périodique
        if (i % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 0));
        }
      }
  
      const mp3buf = mp3encoder.flush();
      if (mp3buf.length > 0) {
        mp3Data.push(mp3buf);
      }
  
      return new Blob(mp3Data, { type: 'audio/mp3' });
    } catch (error) {
      console.error('Erreur lors de l\'encodage MP3:', error);
      throw error;
    }
  }
  
  /**
   * Convertit un élément audio en MP3
   */
  export async function convertToMp3(audioElement, progressCallback) {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    try {
      const response = await fetch(audioElement.src);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      const channelData = audioBuffer.getChannelData(0);
      
      return await optimizedMp3Encode(
        channelData,
        audioBuffer.sampleRate,
        1,
        progressCallback
      );
      
    } catch (error) {
      console.error('Erreur lors de la conversion:', error);
      throw error;
    } finally {
      await audioContext.close();
    }
  }
  
  /**
   * Convertit un AudioBuffer en MP3
   */
  export async function convertToMp3FromAudioBuffer(audioBuffer, progressCallback) {
    const channelData = audioBuffer.getChannelData(0);
    return await optimizedMp3Encode(
      channelData,
      audioBuffer.sampleRate,
      1,
      progressCallback
    );
  }
  
  /**
   * Détecte le type MIME supporté pour l'enregistrement
   */
  export function getSupportedMimeType() {
    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
    const isMobile = /mobile|tablet|android/i.test(navigator.userAgent);
  
    if (isFirefox && isMobile && MediaRecorder.isTypeSupported('audio/webm')) {
      console.log('Firefox mobile détecté - utilisation de WebM');
      return 'audio/webm';
    }
    
    let possibleTypes = [
      'audio/ogg; codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/aac'
    ];
    
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      possibleTypes = [
        'audio/mp4',
        'audio/aac',
        'audio/webm',
        'audio/ogg; codecs=opus'
      ];
    }
    
    for (const type of possibleTypes) {
      try {
        if (MediaRecorder.isTypeSupported(type)) {
          console.log(`Type MIME utilisé ${type}`);
          return type;
        }
      } catch (e) {
        console.warn(`Erreur lors du test du type ${type}:`, e);
      }
    }
    
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      return 'audio/mp4';
    }
    
    if (isFirefox && isMobile) {
      return 'audio/ogg; codecs=opus';
    }
    
    throw new Error('Aucun format audio supporté n\'a été trouvé');
  }
  
  /**
   * Retourne les paramètres audio optimaux selon le type MIME
   */
  export function getOptimalAudioSettings(mimeType) {
    const isMobile = /mobile|tablet|android/i.test(navigator.userAgent);
    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
    
    if (isFirefox && isMobile && mimeType.includes('opus')) {
      return {
        audioBitsPerSecond: 96000,
        sampleRate: 44100,
        channelCount: 1
      };
    }
  
    const settings = {
      'audio/mp4': {
        audioBitsPerSecond: 96000,
        sampleRate: 44100,
        channelCount: 1
      },
      'audio/ogg; codecs=opus': {
        audioBitsPerSecond: isMobile ? 96000 : 128000,
        sampleRate: 48000,
        channelCount: 1
      },
      'audio/webm': {
        audioBitsPerSecond: isMobile ? 96000 : 128000,
        sampleRate: 48000,
        channelCount: 1
      },
      'audio/aac': {
        audioBitsPerSecond: 96000,
        sampleRate: 44100,
        channelCount: 1
      }
    };
  
    return settings[mimeType] || {
      audioBitsPerSecond: isMobile ? 96000 : 128000,
      sampleRate: 44100,
      channelCount: 1
    };
  }